import React from 'react';
import { CheckCircle } from 'lucide-react';

interface CSVData {
  headers: string[];
  rows: any[];
  mappedHeaders: { [key: string]: string };
}

interface MappingStepProps {
  csvData: CSVData | null;
  columnMapping: {
    filename: string;
    title: string;
    description: string;
    keywords: string;
  };
  onColumnMappingChange: (mapping: {
    filename: string;
    title: string;
    description: string;
    keywords: string;
  }) => void;
  onContinue: () => void;
  onBack: () => void;
  isProcessing: boolean;
  hasVisitedEditor: boolean;
}

const MappingStep: React.FC<MappingStepProps> = ({
  csvData,
  columnMapping,
  onColumnMappingChange,
  onContinue,
  onBack,
  isProcessing,
  hasVisitedEditor
}) => {
  // Check how many fields were auto-detected
  const autoDetectedFields = Object.entries(columnMapping).filter(([_, value]) => value !== '').length;
  const hasAutoDetection = autoDetectedFields > 0;

  const handleBackClick = () => {
    if (hasVisitedEditor) {
      alert('Cannot go back after entering editor step. Your CSV data would be lost.');
      return;
    }
    onBack();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Map CSV Columns</h1>
        <p className="text-lg text-gray-600 mb-4">
          Map your CSV columns to the required fields
        </p>

        {/* Auto-detection status */}
        {hasAutoDetection && (
          <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-green-800">
            <CheckCircle className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">
              Auto-detected {autoDetectedFields} field{autoDetectedFields > 1 ? 's' : ''} • You can adjust manually if needed
            </span>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Available Columns:</h3>
          <div className="flex flex-wrap gap-2">
            {csvData?.headers.map((header, index) => {
              const isMapped = Object.values(columnMapping).includes(header);
              return (
                <span
                  key={index}
                  className={`px-3 py-1 rounded-full text-sm ${
                    isMapped
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  title={
                    isMapped
                      ? 'This column will be mapped to a standard field'
                      : 'This column will be preserved as additional data'
                  }
                >
                  {header}
                  {isMapped && <span className="ml-1">✓</span>}
                </span>
              );
            })}
          </div>
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>✓ Mapped columns:</strong> Will be used for AI operations and editing<br/>
              <strong>• Unmapped columns:</strong> Will be preserved and editable but not affected by AI operations
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { key: 'filename', label: 'Filename', required: true },
            { key: 'title', label: 'Title', required: true },
            { key: 'description', label: 'Description', required: true },
            { key: 'keywords', label: 'Keywords', required: true }
          ].map(({ key, label, required }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
              </label>
              <select
                value={columnMapping[key as keyof typeof columnMapping]}
                onChange={(e) => onColumnMappingChange({
                  ...columnMapping,
                  [key]: e.target.value
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select column...</option>
                {csvData?.headers.map((header, index) => (
                  <option key={index} value={header}>{header}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleBackClick}
            disabled={hasVisitedEditor}
            className={`mr-4 px-6 py-2 transition-colors ${
              hasVisitedEditor
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            title={hasVisitedEditor ? 'Cannot go back after entering editor step' : 'Go back to upload'}
          >
            Back
          </button>
          <button
            onClick={onContinue}
            disabled={isProcessing}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : 'Continue to Editor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MappingStep;

